import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    Output,
    ViewChild,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel, EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { BannersConfig, EpcotConfig, MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { ResizeObserverService } from '@frontend/sports/common/core/utils/dom';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { ParticipantImageComponent } from '@frontend/sports/participant-image/feature';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { asapScheduler, debounceTime, distinctUntilChanged, map, take } from 'rxjs';

import { SourceOfPick } from '../betslip-base/models/pick-models';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroupService, PlayerStatsOptionGroup } from './option-group.service';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState } from './option-panel-bottom-action.component';
import { OptionComponent } from './option.component';
import { SHEET_VIEW_OPEN } from './sheet.token';
import { TeamGroupingTabBarComponent } from './team-grouping/team-grouping-tab-bar.component';

@Component({
    selector: 'ms-player-stats-option-group',
    templateUrl: './player-stats-option-group.component.html',
    styleUrl: './player-stats-option-group.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        NgTemplateOutlet,
        MarketHelpComponent,
        TeamGroupingTabBarComponent,
        ScrollAdapterComponent,
        OptionComponent,
        ParticipantImageComponent,
        OptionPanelBottomActionComponent,
    ],
})
export class PlayerStatsOptionGroupComponent implements OnChanges, AfterViewInit {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() showMarketHelp: boolean;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;
    @Output() showAllRequested = new EventEmitter<void>();

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));

    @HostBinding('class') get className(): string {
        const className = 'player-stats-var-scope';

        return this.isEpcotEnabled ? className + ' epcot-enabled' : className;
    }

    // All the props that are depending upon the optionGroup are declared as signals.
    expanded = signal(false);
    headers = signal<(string | undefined)[]>([]);
    optionGroups = signal<PlayerStatsOptionGroup[]>([]);
    showHeaders = computed(() => !!this.headers().filter((x) => x).length && !this.isEpcotEnabled);
    showMoreLessButtonVisible = computed(() => this.optionGroups().length > this.showMoreThreshold());
    visibleOptionsLimit = computed(() => {
        return this.isInSheetView || this.expanded() ? undefined : this.showMoreThreshold();
    });

    optionsClass = computed(() => {
        const className = this.headers().length > 1 ? 'multi' : 'single';
        if (this.headers().filter((x) => x).length === 0) {
            return className + ' no-border';
        }

        return className;
    });

    containerClass = signal('');
    isTeamGroupingEnabled: boolean;
    controllerTabName?: string;
    isEpcotEnabled: boolean;
    iconSize = this.bannerConfig.showLearnMoreButton ? 16 : 12;

    private threshold = this.marketGroupingConfig.showMoreRegularThreshold;
    private showMoreThreshold = computed(() =>
        this.showHeaders() ? (this.optionsLimitOverride ?? this.threshold) + 1 : (this.optionsLimitOverride ?? this.threshold),
    );

    showMoreData = computed(() => {
        const optionCount = this.optionGroups().length;

        return {
            totalCount: optionCount,
            initialVisibleCount: this.showMoreThreshold(),
            pageSize: optionCount,
        };
    });

    @ViewChild(ScrollAdapterComponent, { static: true }) private scrollAdapter: ScrollAdapterComponent;
    @ViewChild('groupContainer', { static: true }) private groupContainer: ElementRef<HTMLDivElement>;
    private readonly CONTAINER_BREAKDOWN_WIDTH = 500;
    private readonly SEXTUPLE_COLUMNS = 6;
    private readonly LARGE_CONTAINER_CLASS = 'large-container';
    private readonly SMALL_CONTAINER_CLASS = 'small-container';
    private readonly SEXTUPLE_CONTAINER_CLASS = 'sextuple-container';
    private readonly TIMEOUT_TO_SET_ARROWS = 500;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public sitecore: Sitecore,
        private marketGroupingConfig: MarketGroupingConfig,
        private optionGroupService: OptionGroupService,
        private epcotConfig: EpcotConfig,
        private timerService: TimerService,
        private resizeObserver: ResizeObserverService,
        private bannerConfig: BannersConfig,
    ) {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    ngOnChanges(changes: ISimpleChanges<PlayerStatsOptionGroupComponent>): void {
        if (changes.event || changes.optionGroup) {
            this.isTeamGroupingEnabled = this.optionGroupService.isTeamGroupingEnabled(
                this.event,
                this.optionGroup,
                this.marketGroupingConfig.teamGroupingMaxNumberOfLines,
            );
            if (this.isTeamGroupingEnabled) {
                this.sourceOfPick = SourceOfPick.TeamGrouping;
            }
            this.headers.set(this.optionGroupService.getOptionHeaders(this.optionGroup.options));
            this.setOptionGroups(this.optionGroup);
            this.setContainerClass();
        }
    }

    ngAfterViewInit(): void {
        this.timerService
            .timer(this.TIMEOUT_TO_SET_ARROWS)
            .pipe(take(1))
            .subscribe(() => this.scrollAdapter?.setArrows());

        this.resizeObserver
            .observe(this.groupContainer)
            .pipe(
                map((observer) => observer.contentRect.width),
                distinctUntilChanged(),
                debounceTime(0, asapScheduler),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.setContainerClass());
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.expanded.set(state !== 'collapsed');
    }

    onTeamGroupingChange(teamGrouping: { optionGroup: EventOptionGroup; controllerTabName?: string }): void {
        this.controllerTabName = teamGrouping.controllerTabName;
        this.headers.set(this.optionGroupService.getOptionHeaders(teamGrouping.optionGroup.options));
        this.setOptionGroups(teamGrouping.optionGroup);
        this.setContainerClass();
    }

    getTrackingInfo(name: string): TrackingSource {
        return {
            source: this.tracking,
            moduleType: this.trackingModuleType,
            controllerTabName: this.controllerTabName,
            playersName: name,
        };
    }

    trackOption(index: number, option: unknown) {
        if (option instanceof EventOption) {
            return option.id;
        }

        return index;
    }

    private setOptionGroups(optionGroup: EventOptionGroup): void {
        this.optionGroups.set(
            this.optionGroupService.getPlayerStatsOptionGroups(optionGroup, this.event, this.headers(), this.showHeaders(), this.isEpcotEnabled),
        );
    }

    private setContainerClass(): void {
        if (this.groupContainer?.nativeElement.clientWidth < this.CONTAINER_BREAKDOWN_WIDTH) {
            this.containerClass.set(this.SMALL_CONTAINER_CLASS);
        } else {
            this.containerClass.set(this.headers().length === this.SEXTUPLE_COLUMNS ? this.SEXTUPLE_CONTAINER_CLASS : this.LARGE_CONTAINER_CLASS);
        }
    }

    protected readonly console = console;
}
